import {
  Matrix3,
  MeshPhysicalMaterial,
  RepeatWrapping,
  ShaderChunk,
  type Texture,
} from '@seddi/three';
import * as flagsModule from '../flags/flagsModule';
import { FeatureFlag } from '../flags/flagsTypes';
import { materialMapsKeys } from '../three/materials/materialConstants';

// List of repeatable textures keys from MeshPhysicalMaterial
const repeatableTextureKeys = [
  'alphaMap',
  'anisotropyMap',
  'heightMap',
  'map',
  'metalnessMap',
  'normalMap',
  'roughnessMap',
  'sheenColorMap',
  'sheenRoughnessMap',
  'specularColorMap',
  'specularIntensityMap',
  'thicknessMap',
  'transmissionMap',
] as const;

// Fabric specular factor textures are not packed in Khronos standard manner.
const lightsPhysicalFragment = ShaderChunk.lights_physical_fragment.replace(
  'texture2D( specularIntensityMap, vSpecularIntensityMapUv ).a',
  'texture2D( specularIntensityMap, vSpecularIntensityMapUv ).r',
);

// Lighter base colors receive less ambient occlusion: AO is blended toward 1.0 (no occlusion)
// using average channel brightness (equal-weighted, so blue-tinted whites score as high as neutral
// whites) raised to a power so the effect only kicks in near white.
// The coefficients (0.2126, 0.7152, 0.0722) are calibrated to the sRGB primaries via BT.709
const aoMapFragment = ShaderChunk.aomap_fragment.replace(
  'reflectedLight.indirectDiffuse *= ambientOcclusion;',
  `float baseLightness = min( pow(  dot( diffuseColor.rgb, vec3( 0.25, 0.6, 0.15 ) ), 4.0 ), 0.7 );
\tambientOcclusion = mix( ambientOcclusion, 1.0, baseLightness );
\treflectedLight.indirectDiffuse *= ambientOcclusion;`,
);

/**
 * Initialize all configurations of a fabric material.
 * @param self - Fabric material.
 */
export function configure(self: MeshPhysicalMaterial) {
  self.alphaTest = 0.04;

  // Inject shader modifications.
  self.onBeforeCompile = (parameters) => {
    parameters.fragmentShader = parameters.fragmentShader.replace(
      '#include <lights_physical_fragment>',
      lightsPhysicalFragment,
    );
    parameters.fragmentShader = parameters.fragmentShader.replace(
      '#include <aomap_fragment>',
      aoMapFragment,
    );
  };

  self.userData = { fabric: true };

  if (!flagsModule.isEnabled(FeatureFlag.DISABLE_TRANSPARENCY_ANGLE_BASED_COMPENSATION)) {
    self.defines.USE_TRANSPARENCY_ANGLE_BASED_COMPENSATION = '';
  }

  if (!flagsModule.isEnabled(FeatureFlag.DISABLE_TRANSPARENCY_FLAT_COMPENSATION)) {
    self.defines.USE_TRANSPARENCY_FLAT_COMPENSATION = '';
  }

  // if (flagsModule.isEnabled(FeatureFlag.PARALLAX) && desc.params.displacementMap) {
  //   // @ts-expect-error: Parallax properties
  //   self.parallaxActive = true;
  //   const fabricThicknessTag = desc.tags?.find((t) => t.includes('FABRIC_THICKNESS_')) || '';
  //   let fabricThickness = 0;
  //   if (fabricThicknessTag) {
  //     fabricThickness = parseFloat(fabricThicknessTag.replace('FABRIC_THICKNESS_', ''));
  //   }
  //   // @ts-expect-error: Parallax properties
  //   material.parallaxScale = Math.min(fabricThickness / 1000, 0.003);
  // }
}

export function configureTextures(self: MeshPhysicalMaterial) {
  // Enable repeat wrapping on all textures
  for (const key of repeatableTextureKeys) {
    const texture = self[key] as Texture | null;

    if (!texture) continue;

    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
  }
}

export function updateRepeatTexturesTransform(self: MeshPhysicalMaterial, matrix: Matrix3) {
  for (const key of repeatableTextureKeys) {
    const texture = self[key] as Texture | null;

    if (!texture) continue;

    texture.matrix.copy(matrix);
    texture.matrixAutoUpdate = false;
  }
}

/**
 * Return a new fabric material with copied values from this instance.
 * @param self - Fabric material.
 */
export function clone(self: MeshPhysicalMaterial): MeshPhysicalMaterial {
  const dst = self.clone();

  // Clone defines not cloned by the default behavior.
  dst.defines = self.defines;

  // Inject shader modifications.
  dst.onBeforeCompile = (parameters) => {
    parameters.fragmentShader = parameters.fragmentShader.replace(
      '#include <lights_physical_fragment>',
      lightsPhysicalFragment,
    );
    parameters.fragmentShader = parameters.fragmentShader.replace(
      '#include <aomap_fragment>',
      aoMapFragment,
    );
  };

  // Create shallow clones of the Texture object, which shares the same image data but allows for individual modification of texture properties
  for (const key of materialMapsKeys) {
    const texture = self[key] as Texture | null;

    if (!texture) continue;

    const textureCopy = texture.clone();
    textureCopy.needsUpdate = true;
    dst[key] = textureCopy;
  }

  return dst;
}
